/**
 * SSE Context
 *
 * Provides real-time event streaming using Server-Sent Events.
 * Uses @microsoft/fetch-event-source for robust reconnection and proper token handling.
 */

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { toast } from '@/utils/toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { getAccessToken } from '@/services/api';
import { teamQueryKeys, portfolioNotificationKeys, getEffectiveTeamId } from '@/lib/queryKeys';
import { useReleaseCrossTabSync } from '@/lib/releaseSync';
import { createReleaseInvalidationDebouncer } from '@/lib/releaseInvalidationDebouncer';
import { devLog } from '@/utils/logger';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { usePostsStore } from '@/stores/postsStore';
import { useBulkImportStore } from '@/stores/bulkImportStore';
import { translateNotification } from '@/utils/notificationUtils';
import { checkAndRepairPushSubscription } from '@/lib/pushNotifications';

// SSE Event Types (must match backend)
const SSE_EVENTS = {
  CONNECTED: 'connected',
  CANVA_IMPORT_COMPLETED: 'canva:import:completed',
  CANVA_IMPORT_FAILED: 'canva:import:failed',
  CANVA_IMPORT_PROGRESS: 'canva:import:progress',
  BULK_IMPORT_PROGRESS: 'bulk:import:progress',
  BULK_IMPORT_COMPLETED: 'bulk:import:completed',
  BULK_IMPORT_FAILED: 'bulk:import:failed',
  NOTIFICATION: 'notification:new',
  RELEASE_COMMENT_REACTION: 'release:comment-reaction',
  ANALYTICS_REFRESH_COMPLETE: 'analytics:refresh:complete',
} as const;

// Custom error classes for retry control
class RetriableError extends Error {}
class FatalError extends Error {}

// Import result for modal display
interface CanvaImportResult {
  status: 'completed' | 'failed' | 'partial';
  totalDesigns: number;
  successCount: number;
  failureCount: number;
  title: string;
  body: string;
  successfulDesigns?: string[];
  failedDesigns?: Array<{ name: string; error: string }>;
}

export interface SSEContextValue {
  isConnected: boolean;
  connectionError: string | null;
}

const SSEContext = createContext<SSEContextValue | null>(null);

interface SSEProviderProps {
  children: React.ReactNode;
}

export function SSEProvider({ children }: SSEProviderProps) {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const teamId = getEffectiveTeamId(user);
  useReleaseCrossTabSync(); // other tabs' release mutations -> same invalidation here

  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<CanvaImportResult | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const reconnectAttempts = useRef(0);
  const isConnectingRef = useRef(false);
  const MAX_RECONNECT_ATTEMPTS = 20; // More generous for server restarts

  // Refs to hold latest values (avoid stale closures in fetchEventSource)
  const queryClientRef = useRef(queryClient);
  const teamIdRef = useRef(teamId);
  const tRef = useRef(t);
  const releaseInvalidatorRef = useRef<ReturnType<typeof createReleaseInvalidationDebouncer> | null>(null);
  if (releaseInvalidatorRef.current === null) {
    releaseInvalidatorRef.current = createReleaseInvalidationDebouncer();
  }

  /**
   * Invalidate queries after import
   */
  const invalidateImportQueries = useCallback(() => {
    const currentTeamId = teamIdRef.current;
    const currentQueryClient = queryClientRef.current;

    // Invalidate React Query caches
    releaseInvalidatorRef.current?.schedule(currentQueryClient, { teamId: currentTeamId });
    currentQueryClient.invalidateQueries({ queryKey: teamQueryKeys.mediaLibrary.all(currentTeamId) });
    // Creator's Zustand list + stats are refreshed by invalidateReleaseState (refreshIfActive).
  }, []);

  /**
   * Handle import completed event
   */
  const handleImportCompleted = useCallback((data: {
    successCount: number;
    totalDesigns: number;
    failureCount: number;
    title?: string;
    body?: string;
  }) => {
    devLog.info('SSE: Canva import completed', data);

    // Show success toast
    const message = data.body || String(t('canva_import_completed_body', { count: data.successCount }));
    toast.success(message);

    invalidateImportQueries();
  }, [t, invalidateImportQueries]);

  /**
   * Handle import failed event - shows modal instead of just toast
   */
  const handleImportFailed = useCallback((data: {
    successCount: number;
    totalDesigns: number;
    failureCount: number;
    title?: string;
    body?: string;
    successfulDesigns?: string[];
    failedDesigns?: Array<{ name: string; error: string }>;
  }) => {
    devLog.info('SSE: Canva import failed', data);

    const isPartial = data.successCount > 0;
    const title = data.title || (isPartial
      ? t('canva_import_partial_title', { defaultValue: 'Import Completed with Errors' })
      : t('canva_import_failed_title'));
    const body = data.body || (isPartial
      ? t('canva_import_partial_body', {
          successCount: data.successCount,
          totalCount: data.totalDesigns,
          failureCount: data.failureCount,
        })
      : t('canva_import_failed_body', { count: data.failureCount }));

    // Show modal with details
    setImportResult({
      status: isPartial ? 'partial' : 'failed',
      totalDesigns: data.totalDesigns,
      successCount: data.successCount,
      failureCount: data.failureCount,
      title: String(title),
      body: String(body),
      successfulDesigns: data.successfulDesigns,
      failedDesigns: data.failedDesigns,
    });

    invalidateImportQueries();
  }, [t, invalidateImportQueries]);

  // Keep refs up-to-date
  useEffect(() => {
    queryClientRef.current = queryClient;
    teamIdRef.current = teamId;
    tRef.current = t;
  }, [queryClient, teamId, t]);

  // Ref to hold latest handlers (avoid stale closures)
  const handlersRef = useRef({
    handleImportCompleted,
    handleImportFailed,
    invalidateImportQueries,
  });

  useEffect(() => {
    handlersRef.current = {
      handleImportCompleted,
      handleImportFailed,
      invalidateImportQueries,
    };
  }, [handleImportCompleted, handleImportFailed, invalidateImportQueries]);

  /**
   * Connect to SSE using fetchEventSource
   */
  const connectSSE = useCallback(async () => {
    const token = getAccessToken();
    if (!token || !user) {
      devLog.debug('SSE: Not connecting - no auth');
      return;
    }

    if (isConnectingRef.current) {
      devLog.debug('SSE: Already connecting, skipping duplicate call');
      return;
    }

    // Abort previous connection if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    isConnectingRef.current = true;

    // Empty string means relative URLs (set in production .env);
    // undefined (env var not set) falls back to empty string (relative) for SSE.
    const RAW = import.meta.env.VITE_API_URL;
    const baseUrl = RAW !== undefined ? RAW : '';
    const sseUrl = `${baseUrl}/api/sse/events`;

    devLog.info('SSE: Connecting...', { url: sseUrl });

    try {
      await fetchEventSource(sseUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        // Custom fetch to inject fresh token on every reconnect attempt
        // The library captures headers once, so stale JWTs cause 401 on reconnect
        fetch: (input, init) => {
          const freshToken = getAccessToken();
          const updatedInit = {
            ...init,
            headers: {
              ...(init?.headers || {}),
              'Authorization': `Bearer ${freshToken}`,
            },
          };
          return window.fetch(input, updatedInit);
        },
        signal: abortController.signal,
        openWhenHidden: true,

        async onopen(response) {
          if (response.ok) {
            devLog.info('SSE: Connection opened');
            setIsConnected(true);
            setConnectionError(null);
            reconnectAttempts.current = 0;
            isConnectingRef.current = false;
            return;
          }

          if (response.status === 401 || response.status === 403) {
            devLog.error('SSE: Auth failed', { status: response.status });
            throw new FatalError(`Authentication failed: ${response.status}`);
          }

          // Other 4xx (except 408/429) will not fix themselves on retry: stop instead of hammering.
          if (response.status >= 400 && response.status < 500 && response.status !== 408 && response.status !== 429) {
            devLog.error('SSE: Client error, not retrying', { status: response.status });
            throw new FatalError(`SSE rejected: ${response.status}`);
          }

          devLog.warn('SSE: Non-OK response', { status: response.status });
          throw new RetriableError(`HTTP ${response.status}`);
        },

        onmessage(event) {
          const eventType = event.event;
          const data = event.data ? JSON.parse(event.data) : null;

          switch (eventType) {
            case SSE_EVENTS.CONNECTED:
              try {
                devLog.info('SSE: Connected', { clientId: data?.clientId });
                setIsConnected(true);
                setConnectionError(null);
                reconnectAttempts.current = 0;

                // Invalidate key queries to catch up on any events missed during downtime
                const currentTeamId = teamIdRef.current;
                const currentQueryClient = queryClientRef.current;
                // Catch up on anything missed while disconnected (release state included)
                releaseInvalidatorRef.current?.schedule(currentQueryClient, { teamId: currentTeamId });
              } catch (error) {
                devLog.error('SSE: Failed to parse connected event', error);
              }
              break;

            case SSE_EVENTS.CANVA_IMPORT_COMPLETED:
              try {
                handlersRef.current.handleImportCompleted(data);
              } catch (error) {
                devLog.error('SSE: Failed to parse canva import completed event', error);
              }
              break;

            case SSE_EVENTS.CANVA_IMPORT_FAILED:
              try {
                handlersRef.current.handleImportFailed(data);
              } catch (error) {
                devLog.error('SSE: Failed to parse canva import failed event', error);
              }
              break;

            case SSE_EVENTS.BULK_IMPORT_PROGRESS:
              try {
                devLog.info('SSE: Bulk import progress', data);
                useBulkImportStore.getState().updateProgress(data);
              } catch (error) {
                devLog.error('SSE: Failed to parse bulk import progress event', error);
              }
              break;

            case SSE_EVENTS.BULK_IMPORT_COMPLETED:
              try {
                devLog.info('SSE: Bulk import completed', data);
                useBulkImportStore.getState().updateProgress({ ...data, status: 'COMPLETED' });
                handlersRef.current.invalidateImportQueries();
              } catch (error) {
                devLog.error('SSE: Failed to parse bulk import completed event', error);
              }
              break;

            case SSE_EVENTS.BULK_IMPORT_FAILED:
              try {
                devLog.info('SSE: Bulk import failed', data);
                useBulkImportStore.getState().updateProgress({ ...data, status: 'FAILED' });
              } catch (error) {
                devLog.error('SSE: Failed to parse bulk import failed event', error);
              }
              break;

            case SSE_EVENTS.NOTIFICATION:
              try {
                const notificationData = data as {
                  type: string;
                  title: string;
                  message?: string;
                  metadata?: Record<string, unknown>;
                  timestamp?: number;
                };
                devLog.info('SSE: New notification', notificationData);

                const currentTeamId = teamIdRef.current;
                const currentQueryClient = queryClientRef.current;
                const rawPostId = notificationData.metadata?.postId;
                const eventPostId =
                  typeof rawPostId === 'number'
                    ? rawPostId
                    : typeof rawPostId === 'string' && /^\d+$/.test(rawPostId)
                      ? Number(rawPostId)
                      : undefined;

                const rawEventTeamId = notificationData.metadata?.teamId;
                const eventTeamId =
                  typeof rawEventTeamId === 'number' && Number.isFinite(rawEventTeamId)
                    ? rawEventTeamId
                    : typeof rawEventTeamId === 'string' && /^\d+$/.test(rawEventTeamId)
                      ? Number(rawEventTeamId)
                      : undefined;
                // Event may belong to another brand than the viewer's effective team
                const alsoTeamIds = eventTeamId !== undefined && eventTeamId !== currentTeamId ? [eventTeamId] : undefined;

                // Invalidate notification queries to update the bell
                currentQueryClient.invalidateQueries({ queryKey: teamQueryKeys.notifications.all(currentTeamId) });
                currentQueryClient.invalidateQueries({ queryKey: portfolioNotificationKeys.all });

                // Show toast for important notifications
                const { type, title, message, timestamp } = notificationData;

                // Translate notification for user's language
                const translated = translateNotification(type, title, message || '', notificationData.metadata, tRef.current);

                // Determine toast type based on notification type
                if (type === 'POST_FAILED' || type === 'APPROVAL_REJECTED') {
                  toast.error(
                    translated.body ? `${translated.title}: ${translated.body}` : translated.title,
                    { duration: 6000 }
                  );
                } else if (type === 'POST_PUBLISHED' || type === 'APPROVAL_APPROVED') {
                  toast.success(
                    translated.body ? `${translated.title}: ${translated.body}` : translated.title,
                    { duration: 4000 }
                  );
                } else {
                  // Default toast for other notification types
                  toast.info(
                    translated.body ? `${translated.title}: ${translated.body}` : translated.title,
                    { duration: 4000 }
                  );
                }

                // Refresh calendar when post is published/failed (same invalidations as Calendar.tsx shoot handler)
                if (type === 'POST_PUBLISHED' || type === 'POST_FAILED') {
                  devLog.info('SSE: Refreshing calendar after publish', { type, teamId: currentTeamId, timestamp });
                  releaseInvalidatorRef.current?.schedule(currentQueryClient, { teamId: currentTeamId, alsoTeamIds, postId: eventPostId });

                  // Update lastPublishTimestamp to trigger Calendar's day drawer refresh
                  usePostsStore.getState().setLastPublishTimestamp(timestamp || Date.now());
                }

                // Refresh post list when variations are auto-translated
                if (type === 'VARIATIONS_AUTO_TRANSLATED') {
                  releaseInvalidatorRef.current?.schedule(currentQueryClient, { teamId: currentTeamId, alsoTeamIds, postId: eventPostId });
                }

                // Refresh posts, approvals, and calendar when approval status changes
                if (
                  type === 'APPROVAL_APPROVED' ||
                  type === 'APPROVAL_REJECTED' ||
                  type === 'APPROVAL_CHANGES_REQUESTED' ||
                  type === 'APPROVAL_REQUESTED' ||
                  type === 'APPROVAL_RESUBMITTED' ||
                  type === 'APPROVAL_WITHDRAWN' ||
                  type === 'APPROVAL_COMMENT' ||
                  type === 'APPROVAL_MENTION'
                ) {
                  // Comments are status-neutral (reviewer "changes requested" arrives as
                  // APPROVAL_CHANGES_REQUESTED): light scope avoids refetching posts/calendar.
                  devLog.info('SSE: Refreshing data after approval event', { type, teamId: currentTeamId });
                  // Single cache-sync entry point (also refreshes the Creator Zustand list)
                  releaseInvalidatorRef.current?.schedule(currentQueryClient, {
                    teamId: currentTeamId,
                    alsoTeamIds,
                    postId: eventPostId,
                    scope: type === 'APPROVAL_COMMENT' || type === 'APPROVAL_MENTION' ? 'comment' : 'full',
                  });
                }
              } catch (error) {
                devLog.error('SSE: Failed to parse notification event', error);
              }
              break;

            case SSE_EVENTS.RELEASE_COMMENT_REACTION:
              // Silent: refresh the chat only (no toast, no notification). The server already scopes
              // delivery (internal-only threads go to staff only); the payload carries no comment id.
              try {
                const reaction = data as { postId?: unknown; teamId?: unknown } | null;
                const currentTeamId = teamIdRef.current;
                const currentQueryClient = queryClientRef.current;
                const eventPostId =
                  typeof reaction?.postId === 'number' && Number.isFinite(reaction.postId)
                    ? reaction.postId
                    : undefined;
                const eventTeamId =
                  typeof reaction?.teamId === 'number' && Number.isFinite(reaction.teamId)
                    ? reaction.teamId
                    : undefined;
                // Event may belong to another brand than the viewer's effective team (same as other release events)
                const alsoTeamIds = eventTeamId !== undefined && eventTeamId !== currentTeamId ? [eventTeamId] : undefined;
                if (eventPostId !== undefined) {
                  releaseInvalidatorRef.current?.schedule(currentQueryClient, {
                    teamId: currentTeamId,
                    alsoTeamIds,
                    postId: eventPostId,
                    scope: 'comment',
                  });
                }
              } catch (error) {
                devLog.error('SSE: Failed to handle comment reaction event', error);
              }
              break;

            case SSE_EVENTS.ANALYTICS_REFRESH_COMPLETE:
              try {
                devLog.info('SSE: Analytics refresh complete', data);
                const currentTeamId = teamIdRef.current;
                const currentQueryClient = queryClientRef.current;
                // Invalidate analytics queries so drawer/table auto-refresh
                currentQueryClient.invalidateQueries({ queryKey: teamQueryKeys.analytics.all(currentTeamId) });
              } catch (error) {
                devLog.error('SSE: Failed to handle analytics refresh event', error);
              }
              break;

            default:
              devLog.debug('SSE: Unhandled event type', { eventType, data });
              break;
          }
        },

        onclose() {
          devLog.info('SSE: Server closed connection');
          setIsConnected(false);
          // Throw to trigger reconnection
          throw new RetriableError('Server closed connection');
        },

        onerror(err) {
          setIsConnected(false);

          if (err instanceof FatalError) {
            devLog.error('SSE: Fatal error, not reconnecting', { error: err.message });
            setConnectionError(err.message);
            isConnectingRef.current = false;
            throw err; // Stop reconnection
          }

          reconnectAttempts.current++;

          if (reconnectAttempts.current > MAX_RECONNECT_ATTEMPTS) {
            devLog.warn('SSE: Max reconnect attempts reached, switching to slow polling');
            setConnectionError('Unable to establish real-time connection. Notifications may be delayed.');
            // Don't throw FatalError — return a long interval instead
            return 30000; // Try every 30 seconds
          }

          // Exponential backoff with jitter
          const baseDelay = Math.min(1000 * Math.pow(2, reconnectAttempts.current - 1), 30000);
          const jitter = Math.random() * 1000;
          const delay = baseDelay + jitter;

          devLog.info(`SSE: Reconnecting in ${Math.round(delay)}ms (attempt ${reconnectAttempts.current})`);
          return delay;
        },
      });
    } catch (error) {
      // fetchEventSource resolves when connection is permanently closed
      devLog.info('SSE: Connection ended', { error: error instanceof Error ? error.message : 'unknown' });
    } finally {
      isConnectingRef.current = false;
    }
  }, [user]);

  // Connect when authenticated
  useEffect(() => {
    if (!user) {
      devLog.debug('SSE: No user, not connecting');
      return;
    }

    connectSSE();

    return () => {
      releaseInvalidatorRef.current?.cancel();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      setIsConnected(false);
      reconnectAttempts.current = 0;
      isConnectingRef.current = false;
    };
  }, [user, teamId, connectSSE]);

  // Visibility-based reconnection (when user returns to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isConnected && user) {
        devLog.info('SSE: Tab visible, checking connection...');
        // Trigger reconnection by aborting current (dead) connection and starting fresh
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        reconnectAttempts.current = 0;
        isConnectingRef.current = false; // Reset so connectSSE doesn't skip
        connectSSE();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isConnected, user, connectSSE]);

  // Push subscription health check - auto-repair expired subscriptions
  useEffect(() => {
    const token = getAccessToken();
    if (!token || !user) return; // Not authenticated

    // Delay slightly to not compete with SSE connection and initial data loading
    const timer = setTimeout(() => {
      checkAndRepairPushSubscription().catch(() => {
        // Silently ignore - push repair is best-effort
      });
    }, 5000); // 5 second delay after auth

    return () => clearTimeout(timer);
  }, [user]);

  const value: SSEContextValue = {
    isConnected,
    connectionError,
  };

  return (
    <SSEContext.Provider value={value}>
      {children}

      {/* Import Result Modal */}
      <Modal
        open={importResult !== null}
        onOpenChange={(open) => !open && setImportResult(null)}
        title={importResult?.title || ''}
        size="sm"
        footer={
          <div className="flex justify-end">
            <Button variant="primary" onClick={() => setImportResult(null)}>
              {t('ok', { defaultValue: 'OK' })}
            </Button>
          </div>
        }
      >
        {importResult && (
          <div className="py-4">
            {/* Status Icon */}
            <div className="flex justify-center mb-4">
              {importResult.status === 'partial' ? (
                <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center">
                  <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              ) : (
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              )}
            </div>

            {/* Stats Summary */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{t('total', { defaultValue: 'Total' })}</span>
                <span className="font-medium">{importResult.totalDesigns}</span>
              </div>
              {importResult.successCount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-green-600">{t('successful', { defaultValue: 'Successful' })}</span>
                  <span className="font-medium text-green-600">{importResult.successCount}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-red-600">{t('failed', { defaultValue: 'Failed' })}</span>
                <span className="font-medium text-red-600">{importResult.failureCount}</span>
              </div>
            </div>

            {/* Successful Designs */}
            {importResult.successfulDesigns && importResult.successfulDesigns.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-green-700 mb-2">
                  {t('successfully_imported', { defaultValue: 'Successfully Imported' })}
                </h4>
                <ul className="space-y-1">
                  {importResult.successfulDesigns.map((name, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      {/* eslint-disable-next-line i18next/no-literal-string */}
                      <span className="text-green-500 flex-shrink-0">✓</span>
                      <span className="break-words">{name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Failed Designs */}
            {importResult.failedDesigns && importResult.failedDesigns.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-red-700 mb-2">
                  {t('failed_to_import', { defaultValue: 'Failed to Import' })}
                </h4>
                <ul className="space-y-2">
                  {importResult.failedDesigns.map((item, idx) => (
                    <li key={idx} className="text-sm">
                      <div className="flex items-start gap-2 text-gray-700">
                        {/* eslint-disable-next-line i18next/no-literal-string */}
                        <span className="text-red-500 flex-shrink-0">✗</span>
                        <span className="break-words font-medium">{item.name}</span>
                      </div>
                      <p className="text-xs text-gray-500 ml-5 mt-0.5">{item.error}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal>
    </SSEContext.Provider>
  );
}

/**
 * Hook to access SSE context
 */
export function useSSE(): SSEContextValue {
  const context = useContext(SSEContext);
  if (!context) {
    throw new Error('useSSE must be used within an SSEProvider');
  }
  return context;
}

export default SSEContext;
